import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaSessionId, setCaptchaSessionId] = useState('');
  const [loading, setLoading] = useState(false);

  // 加载验证码
  const loadCaptcha = async () => {
    try {
      console.log('开始加载验证码...');
      console.log('API Base URL:', api.defaults.baseURL);
      const response = await api.get('/api/captcha');
      console.log('验证码API响应完整对象:', response);
      console.log('response.data:', response.data);
      console.log('response.data类型:', typeof response.data);
      
      // axios返回的response对象，数据在response.data中
      // 后端返回格式: { success: true, data: { captchaSvg: '...', sessionId: '...' } }
      const responseData = response.data;
      console.log('验证码响应数据:', responseData);
      console.log('responseData.success:', responseData?.success);
      console.log('responseData.data:', responseData?.data);
      
      if (responseData && responseData.success && responseData.data) {
        const svgContent = responseData.data.captchaSvg;
        const sessionId = responseData.data.sessionId;
        console.log('设置验证码SVG，长度:', svgContent?.length);
        console.log('SVG内容前100字符:', svgContent?.substring(0, 100));
        console.log('SessionId:', sessionId);
        
        if (svgContent) {
          setCaptchaSvg(svgContent);
          setCaptchaSessionId(sessionId);
          console.log('验证码设置成功');
        } else {
          console.error('SVG内容为空');
          message.error('验证码内容为空，请刷新重试');
        }
      } else {
        console.error('验证码响应格式错误:', responseData);
        message.error('加载验证码失败，请刷新页面重试');
      }
    } catch (error) {
      console.error('加载验证码失败:', error);
      console.error('错误详情:', error.response?.data || error.message);
      message.error('加载验证码失败，请刷新页面重试');
    }
  };

  // 组件加载时获取验证码
  useEffect(() => {
    loadCaptcha();
  }, []);

  // 监听验证码SVG变化，用于调试
  useEffect(() => {
    console.log('验证码SVG状态变化:', {
      hasSvg: !!captchaSvg,
      svgLength: captchaSvg?.length,
      svgPreview: captchaSvg?.substring(0, 50),
      sessionId: captchaSessionId
    });
  }, [captchaSvg, captchaSessionId]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      console.log('开始登录，用户名:', values.username);
      // 调用登录API - api.js的baseURL是getApiBaseUrl()，返回'http://localhost:5001'或''
      // 所以需要完整的路径 '/api/users/login'
      const response = await api.post('/api/users/login', {
        username: values.username,
        password: values.password,
        captchaText: values.captcha,
        captchaSessionId: captchaSessionId
      });
      console.log('登录响应:', response);
      console.log('response.data:', response.data);
      console.log('response.data类型:', typeof response.data);
      
      // api.js的响应拦截器直接返回response对象，所以需要访问response.data
      const responseData = response.data;
      console.log('responseData:', responseData);
      console.log('responseData类型:', typeof responseData);
      console.log('responseData键:', Object.keys(responseData || {}));
      
      // 后端返回格式: { status: 'success', token, data: { user: {...} } }
      // 但api.js的响应拦截器返回的是response对象，所以response.data才是后端返回的数据
      const token = responseData?.token || responseData?.data?.token;
      const user = responseData?.data?.user || responseData?.user;
      
      console.log('尝试提取token:', {
        'responseData?.token': responseData?.token,
        'responseData?.data?.token': responseData?.data?.token,
        '最终token': token
      });
      console.log('尝试提取user:', {
        'responseData?.data?.user': responseData?.data?.user,
        'responseData?.user': responseData?.user,
        '最终user': user
      });
      
      console.log('提取的token:', token);
      console.log('提取的user:', user);
      
      if (!token) {
        console.error('登录失败：未收到token，完整响应:', response);
        console.error('responseData结构:', JSON.stringify(responseData, null, 2));
        message.error('登录失败：未收到token');
        return;
      }
      
      // 保存token和用户信息
      console.log('保存token到localStorage:', token);
      localStorage.setItem('token', token);
      
      if (user) {
        console.log('登录成功，保存用户信息:', user);
        console.log('用户 isAdmin 字段:', user.isAdmin, '类型:', typeof user.isAdmin);
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        console.warn('警告：未收到用户信息，但token已保存');
      }
      
      // 验证保存是否成功
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      console.log('验证保存结果 - token存在:', !!savedToken, 'user存在:', !!savedUser);
      
      message.success('登录成功');
      
      // 登录成功后跳转逻辑：
      // 1. 如果有来源页面（location.state.from），跳转回来源页面
      // 2. 否则默认跳转到首页
      const from = location.state?.from?.pathname || location.state?.from || '/';
      console.log('登录前访问的页面:', location.state?.from);
      console.log('准备跳转到:', from);
      
      // 延迟一下，确保localStorage已保存
      setTimeout(() => {
        console.log('执行跳转，目标:', from);
        navigate(from, { replace: true });
      }, 100);
    } catch (error) {
      console.error('登录错误:', error);
      const errorMessage = error.response?.data?.message || error.message || '登录失败';
      message.error(errorMessage);
      
      // 如果验证码错误，刷新验证码
      if (errorMessage.includes('验证码')) {
        loadCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Form
        name="login"
        className="login-form"
        initialValues={{ remember: true }}
        onFinish={onFinish}
      >
        <h2 style={{ textAlign: 'center', marginBottom: 24 }}>
          铖序员
        </h2>
        
        <Form.Item
          name="username"
          rules={[
            { required: true, message: '请输入用户名' },
            { pattern: /^[a-zA-Z0-9_-]+$/, message: '用户名只能包含英文字母、数字、下划线和连字符' },
            { min: 3, message: '用户名长度不能少于3个字符' },
            { max: 30, message: '用户名长度不能超过30个字符' }
          ]}
        >
          <Input
            prefix={<UserOutlined />}
            placeholder="用户名（英文字母、数字、下划线、连字符）"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[{ required: true, message: '请输入密码' }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="密码"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="captcha"
          label="验证码"
          rules={[{ required: true, message: '请输入验证码' }]}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div 
              style={{ 
                cursor: 'pointer',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fafafa',
                width: '120px',
                height: '40px',
                gap: '8px'
              }}
              onClick={loadCaptcha}
              title="点击刷新验证码"
            >
              {captchaSvg ? (
                <div 
                  dangerouslySetInnerHTML={{ __html: captchaSvg }}
                  style={{ 
                    height: '40px', 
                    width: '120px',
                    display: 'inline-block',
                    lineHeight: 0,
                    verticalAlign: 'middle'
                  }}
                />
              ) : (
                <span style={{ color: '#999', fontSize: '12px', textAlign: 'center' }}>加载中...</span>
              )}
              <ReloadOutlined 
                style={{ 
                  fontSize: '16px',
                  color: '#1890ff',
                  flexShrink: 0
                }} 
              />
            </div>
            <Input
              placeholder="请输入验证码（不区分大小写）"
              size="large"
              maxLength={4}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="login-form-button"
            block
            size="large"
            loading={loading}
          >
            登录
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default Login; 